import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { RefreshCw, Clock, CheckCircle, AlertCircle, Mail, Phone } from 'lucide-react';

const RefundPage = () => {
  const refundTypes = [
    {
      icon: CheckCircle,
      title: '7-Day Money-Back',
      description: 'Full refund within 7 days, no questions asked',
      color: 'green'
    },
    {
      icon: RefreshCw,
      title: 'Technical Issues',
      description: 'Refund for any platform problems',
      color: 'blue'
    },
    {
      icon: Clock,
      title: 'Unused Time',
      description: 'Prorated refund for unused subscription time',
      color: 'purple'
    },
    {
      icon: AlertCircle,
      title: 'Special Cases',
      description: 'We understand life happens - just ask',
      color: 'orange'
    }
  ];

  const sections = [
    {
      title: '1. Our Refund Promise',
      content: `We want you to be completely happy with PhysicsNotebook. If you're not, here's our simple refund policy:
      
      • 7-Day Money-Back: Get a full refund within 7 days, no questions asked
      • Technical Issues: We'll fix them or refund you if we can't
      • Billing Mistakes: We'll correct them immediately
      • Not as Expected? Let us know and we'll make it right`
    },
    {
      title: '2. How to Get a Refund',
      content: `It's simple:
      
      1. Email us at aditya@physicsnotebook.in
      2. Include your name and order details
      3. We'll process it within 2 business days
      
      Most refunds hit your account within 5-7 days.`
    },
    {
      title: '3. When Refunds Don\'t Apply',
      content: `We can't offer refunds for:
      
      • Subscriptions used for more than 7 days
      • Content that's been heavily used
      • Violations of our Terms of Service
      • Requests made 30+ days after purchase`
    },
    {
      title: '4. Partial Refunds',
      content: `In some cases, we can do partial refunds:
      
      • Unused months on annual plans
      • Major service disruptions
      • Special circumstances
      
      Just ask and we'll work with you.`
    },
    {
      title: '5. Need Help?',
      content: `We're here for you:
      
      📧 Email: aditya@physicsnotebook.in
      📱 WhatsApp: +91 9309242445
      
      We usually respond within 24 hours.`
    }
  ];

  return (
    <>
      <Helmet>
        <title>Refund Policy - PhysicsNotebook.in | No-Risk 7-Day Guarantee</title>
        <meta name="description" content="PhysicsNotebook.in offers a 7-day money-back guarantee. Simple, fair refund policy for all our physics learning resources. Your satisfaction is guaranteed." />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-gradient section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Simple, <span className="gradient-text">No-Risk</span> Refunds
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Try PhysicsNotebook risk-free with our 7-day money-back guarantee. No questions asked.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm text-gray-500 mt-4"
          >
            Last updated: August 2025
          </motion.p>
        </div>
      </section>

      {/* Refund Types */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-gray-900 mb-4">Our Refund Promise</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We make it easy to get your money back if you're not 100% satisfied.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {refundTypes.map((type, index) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-gray-50 rounded-xl p-6 card-hover"
              >
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  type.color === 'green' ? 'bg-green-100' :
                  type.color === 'blue' ? 'bg-blue-100' :
                  type.color === 'purple' ? 'bg-purple-100' : 'bg-orange-100'
                }`}>
                  <type.icon className={`h-8 w-8 ${
                    type.color === 'green' ? 'text-green-600' :
                    type.color === 'blue' ? 'text-[#123B7A]' :
                    type.color === 'purple' ? 'text-purple-600' : 'text-orange-600'
                  }`} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{type.title}</h3>
                <p className="text-gray-600 text-sm">{type.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
            <div className="flex items-center mb-2">
              <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
              <p className="text-green-800 font-medium">7-Day Money-Back Guarantee</p>
            </div>
            <p className="text-green-700 text-sm">
              Try PhysicsNotebook.in risk-free! If you're not completely satisfied within the first 7 days, 
              we'll refund your payment in full, no questions asked.
            </p>
          </div>
        </div>
      </section>

      {/* Policy Details */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg"
              >
                <h2 className="font-display text-2xl font-semibold text-gray-900 mb-4">{section.title}</h2>
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Common questions about our refund and cancellation policy.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How long does it take to process a refund?</h3>
              <p className="text-gray-600">Refunds are typically processed within 5-7 business days after approval. The time for the refund to appear in your account depends on your bank or payment provider.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I get a refund after the 7-day period?</h3>
              <p className="text-gray-600">While our standard guarantee is 7 days, we consider refunds for exceptional circumstances such as technical issues, billing errors, or special situations on a case-by-case basis.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What if I cancel my annual subscription mid-year?</h3>
              <p className="text-gray-600">For annual subscriptions cancelled after the 7-day period, we may offer a prorated refund for the unused portion, depending on usage and circumstances.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I request a refund?</h3>
              <p className="text-gray-600">Simply email us at refunds@physicsnotebook.in with your account details and reason for the refund request. Our team will review and respond within 2-3 business days.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-gradient-to-r from-[#123B7A] to-[#0EA5FF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white mb-6">
              Need Help with a Refund?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Our customer support team is here to help you with any refund questions or requests.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:refunds@physicsnotebook.in" className="bg-white text-[#123B7A] hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors">
                Email Refunds Team
              </a>
              <a href="mailto:support@physicsnotebook.in" className="border-2 border-white text-white hover:bg-white hover:text-[#123B7A] px-8 py-3 rounded-lg font-semibold transition-colors">
                Contact Support
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default RefundPage;
